import { Building2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export const Footer = () => {
  const { t } = useI18n();
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#1A1C1A] text-background"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <div className="mb-8">
              <a
                href="#top"
                aria-label="DBR Invest a.s."
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-full hover:bg-secondary hover:text-foreground transition-colors"
              >
                <Building2 className="w-4 h-4" strokeWidth={2.2} />
                <span className="font-heading tracking-tight">
                  DBR&nbsp;Invest&nbsp;a.s.
                </span>
              </a>
              <div className="mt-3 font-heading text-sm tracking-wide text-background/60">
                dbrinvest<span className="text-secondary">.cz</span>
              </div>
            </div>
            <h3 className="font-heading text-3xl md:text-5xl font-semibold leading-[1.05] tracking-tight max-w-xl">
              {t.footer.headline_1}{" "}
              <span className="italic font-light text-secondary">
                {t.footer.headline_em}
              </span>
              {t.footer.headline_q}
            </h3>
            <a
              href="#contact"
              data-testid="footer-cta"
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 bg-background text-foreground rounded-full text-sm font-medium hover:bg-secondary hover:text-foreground transition-colors"
            >
              {t.footer.cta}
            </a>
          </div>

          <div className="md:col-span-3 md:col-start-8">
            <div className="overline text-background/50 mb-6">
              {t.footer.nav}
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="#services"
                  className="hover:text-secondary transition-colors"
                >
                  {t.nav.services}
                </a>
              </li>
              <li>
                <a
                  href="#calculator"
                  className="hover:text-secondary transition-colors"
                >
                  {t.nav.calculator}
                </a>
              </li>
              <li>
                <a
                  href="#about"
                  className="hover:text-secondary transition-colors"
                >
                  {t.nav.about}
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  className="hover:text-secondary transition-colors"
                >
                  {t.nav.contact}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline text-background/50 mb-6">
              {t.footer.contact}
            </div>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="mailto:dbrinvest@email.cz"
                  className="hover:text-secondary transition-colors"
                >
                  dbrinvest@email.cz
                </a>
              </li>
              <li>
                <a
                  href="tel:+420774452773"
                  className="hover:text-secondary transition-colors"
                >
                  +420 774 452 773
                </a>
              </li>
              <li className="text-background/70">
                Banskobystrická 265/185
                <br />
                {t.contact.address_value.split(",").slice(-1)[0].trim()}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-background/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-background/50">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
            <span>{t.footer.rights(year)}</span>
            <a
              href="https://app.emergent.sh/?utm_source=emergent-badge"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-emergent-badge"
              className="inline-flex items-center gap-1.5 text-background/40 hover:text-background/80 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M15.5702 8.13142C15.7729 8.0412 16.0007 8.18878 15.9892 8.4103C15.8374 11.3192 14.0965 14.0405 11.2531 15.3065C8.40964 16.5725 5.2224 16.0453 2.95912 14.2117C2.78676 14.072 2.82955 13.804 3.03219 13.7137L4.95677 12.8568C5.04866 12.8159 5.15446 12.823 5.24204 12.8725C6.73377 13.7153 8.59176 13.8649 10.2772 13.1145C11.9626 12.3641 13.0947 10.8833 13.4665 9.21075C13.4883 9.11256 13.5539 9.02918 13.6457 8.98827L15.5702 8.13142Z" fill="currentColor"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M15.3066 4.74698L15.5067 5.19653C15.5759 5.35178 15.5061 5.53366 15.3508 5.60278L1.29992 11.8586C1.14467 11.9278 0.962794 11.8579 0.893675 11.7027L0.701732 11.2716L0.693457 11.2531C-1.10317 7.21778 0.711626 2.49007 4.74692 0.693443C8.78221 -1.10318 13.51 0.711693 15.3066 4.74698ZM2.82356 8.55367C2.63552 8.63739 2.41991 8.51617 2.40853 8.31065C2.28373 6.05724 3.53858 3.85787 5.72286 2.88536C7.90715 1.91286 10.3813 2.45199 11.9724 4.05256C12.1175 4.19854 12.0633 4.43988 11.8753 4.5236L2.82356 8.55367Z" fill="currentColor"/>
              </svg>
              <span>Made with Emergent</span>
            </a>
          </div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-background transition-colors">
              {t.footer.gdpr}
            </a>
            <a href="#" className="hover:text-background transition-colors">
              {t.footer.terms}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
